import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, EmptyState } from '../ui';
import { FaGhost } from 'react-icons/fa';

/**
 * RatingsSection - Simplified ratings display and form with boop visual style
 * Extracted from PromptDetail for better organization
 */
const RatingsSection = ({
  ratings,
  myRating,
  ratingFormData,
  setRatingFormData,
  onSubmit,
  onDelete,
  currentUser,
  isOwner,
}) => {
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(e);
    setShowForm(false);
  };

  return (
    <div className="bg-white dark:bg-spooky-bg-medium p-6 rounded-lg shadow-md border-2 border-transparent hover:border-boop-primary dark:hover:border-spooky-ghost-glow transition-all duration-300">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <FaGhost className="text-boop-ghost dark:text-spooky-ghost-glow text-2xl animate-float" />
          <h2 className="text-2xl font-semibold dark:text-white">Ratings & Reviews</h2>
        </div>
        {currentUser && !isOwner && (
          <Button
            variant="primary"
            onClick={() => setShowForm(!showForm)}
            className="hover:animate-boop"
          >
            {myRating ? '✏️ Edit My Boop' : '👻 Boop This Prompt!'}
          </Button>
        )}
      </div>

      {/* Rating Form */}
      {showForm && currentUser && (
        <form onSubmit={handleSubmit} className="bg-gradient-to-br from-gray-50 to-boop-light dark:from-spooky-bg-light dark:to-spooky-bg-medium p-6 rounded-lg mb-6 space-y-4 border-2 border-boop-primary dark:border-spooky-purple-mist shadow-lg animate-float">
          <div className="flex items-center gap-2">
            <FaGhost className="text-boop-ghost dark:text-spooky-ghost-glow animate-wiggle" />
            <h3 className="font-semibold dark:text-white text-lg">
              {myRating ? '✨ Update Your Boop' : '👻 Give This Prompt a Boop!'}
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <RatingSlider
              label="👻 Overall Boop"
              value={ratingFormData.overall_rating}
              onChange={(val) => setRatingFormData({ ...ratingFormData, overall_rating: val })}
              primary
              icon="👻"
            />
            <RatingSlider
              label="💎 Clarity"
              value={ratingFormData.clarity_rating}
              onChange={(val) => setRatingFormData({ ...ratingFormData, clarity_rating: val })}
              icon="💎"
            />
            <RatingSlider
              label="✨ Creativity"
              value={ratingFormData.creativity_rating}
              onChange={(val) => setRatingFormData({ ...ratingFormData, creativity_rating: val })}
              icon="✨"
            />
            <RatingSlider
              label="🎯 Effectiveness"
              value={ratingFormData.effectiveness_rating}
              onChange={(val) => setRatingFormData({ ...ratingFormData, effectiveness_rating: val })}
              icon="🎯"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 dark:text-white">
              Comment (Optional)
            </label>
            <textarea
              value={ratingFormData.comment}
              onChange={(e) => setRatingFormData({ ...ratingFormData, comment: e.target.value })}
              placeholder="Share your thoughts about this prompt..."
              className="w-full px-3 py-2 border dark:border-spooky-purple-mist rounded-lg dark:bg-spooky-bg-medium dark:text-white resize-none"
              rows={3}
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button type="submit" variant="primary" className="hover:animate-boop">
              {myRating ? '✨ Update Boop' : '👻 Submit Boop!'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            {myRating && (
              <Button type="button" variant="danger" onClick={onDelete} className="ml-auto">
                🗑️ Delete Rating
              </Button>
            )}
          </div>
        </form>
      )}

      {/* Ratings List */}
      <div className="space-y-4">
        {ratings.length === 0 ? (
          <EmptyState
            icon={FaGhost}
            title="No boops yet"
            message="Be the first to boop this prompt! 👻"
          />
        ) : (
          ratings.map((rating) => (
            <div
              key={rating.id}
              className="border-b dark:border-spooky-purple-mist pb-4 last:border-b-0 hover:bg-gray-50 dark:hover:bg-spooky-bg-light p-3 rounded-lg transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <FaGhost className="text-boop-ghost dark:text-spooky-ghost-glow text-xs" />
                  <Link
                    to={`/profile/${rating.username}`}
                    className="font-semibold hover:text-boop-primary dark:text-white dark:hover:text-boop-primary transition"
                  >
                    @{rating.username}
                  </Link>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(rating.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-boop-primary to-boop-secondary rounded-full shadow-md">
                  <span className="text-lg font-bold text-white drop-shadow-glow">
                    {rating.overall_rating}
                  </span>
                  <span className="text-xs text-white/80">/10</span>
                </div>
              </div>
              <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400 mb-2 ml-6">
                <span className="flex items-center gap-1">
                  💎 <span className="font-medium">{rating.clarity_rating}</span>/10
                </span>
                <span className="flex items-center gap-1">
                  ✨ <span className="font-medium">{rating.creativity_rating}</span>/10
                </span>
                <span className="flex items-center gap-1">
                  🎯 <span className="font-medium">{rating.effectiveness_rating}</span>/10
                </span>
              </div>
              {rating.comment && (
                <p className="text-gray-700 dark:text-gray-300 mt-2 ml-6 italic border-l-4 border-boop-primary dark:border-spooky-purple-mist pl-3">
                  "{rating.comment}"
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/**
 * RatingSlider - Reusable rating input component with boop styling
 */
const RatingSlider = ({ label, value, onChange, primary = false, icon }) => {
  const getColorForValue = (val) => {
    if (val <= 3) return 'text-red-500 dark:text-red-400';
    if (val <= 5) return 'text-yellow-500 dark:text-yellow-400';
    if (val <= 7) return 'text-blue-500 dark:text-blue-400';
    if (val <= 9) return 'text-green-500 dark:text-green-400';
    return 'text-boop-ghost dark:text-spooky-ghost-glow animate-glow';
  };

  const getMessageForValue = (val) => {
    if (val <= 3) return '😢 Needs work';
    if (val <= 5) return '😐 It\'s okay';
    if (val <= 7) return '😊 Pretty good!';
    if (val <= 9) return '🎉 Excellent!';
    return '👻 PERFECT BOOP!';
  };

  return (
    <div className="bg-white dark:bg-spooky-bg-dark p-4 rounded-lg border-2 border-gray-200 dark:border-spooky-purple-mist hover:border-boop-primary dark:hover:border-spooky-ghost-glow transition-all duration-200">
      <label className="block text-sm font-medium mb-3 dark:text-white flex items-center gap-2">
        {label}
      </label>
      <input
        type="range"
        min="1"
        max="10"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full accent-boop-primary h-2 bg-gray-200 dark:bg-spooky-bg-darker rounded-lg appearance-none cursor-pointer hover:scale-105 transition-transform"
        style={{
          background: `linear-gradient(to right, #6366f1 0%, #8b5cf6 ${value * 10}%, #e5e7eb ${value * 10}%, #e5e7eb 100%)`
        }}
      />
      <div className="flex justify-between items-center mt-3">
        <div
          className={`text-2xl font-bold ${getColorForValue(value)} ${
            primary ? 'text-3xl' : ''
          } transition-all duration-200`}
        >
          {value}/10
        </div>
        <div className={`text-xs font-medium ${getColorForValue(value)} transition-all duration-200`}>
          {getMessageForValue(value)}
        </div>
      </div>
    </div>
  );
};

export default RatingsSection;
